import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { EntityItem, EntityPicker } from '@features/entity-picker'
import { useEmployees } from '@hooks/useEmployee'
import { useAdminApprovalGroups } from '@features/admin/hooks/useAdminApprovalGroups'
import { Modal, ModalForm } from 'components/notifications'
import { Field } from 'components/form/field'
import { Input } from 'components/form/input'
import { useUpdateDefaultGroupManager } from '../../../hooks/useApprovalGroupsManagement'

interface Props {
  approvalGroup: AdminApprovalGroup
  onClose: () => void
  onError: () => void
  onSuccess: () => void
}

const WORKDAY_WORKER_ID_PATTERN = /^\d+$/

// The API returns validation failures as { directReportsOfWid: ['...'] }
const serverErrorsFrom = (error: unknown): string[] | undefined => {
  if (!(error instanceof AxiosError) || error.response?.status !== 422) return

  const messages = (
    error.response.data as { directReportsOfWid?: string[] } | undefined
  )?.directReportsOfWid

  return messages?.length ? messages : undefined
}

export const EditDefaultGroupManagerModal: FC<Props> = ({
  approvalGroup,
  onClose,
  onError,
  onSuccess,
}) => {
  const { t } = useTranslation()

  const currentWorkdayWorkerId =
    approvalGroup.defaultForDirectReportsOfEmployee?.workdayWorkerId ?? null

  const [workdayWorkerId, setWorkdayWorkerId] = useState(
    currentWorkdayWorkerId === null ? '' : String(currentWorkdayWorkerId),
  )
  const [errors, setErrors] = useState<string[]>([])
  const [search, setSearch] = useState('')

  const { data: employees = [] } = useEmployees()
  const { data: approvalGroups = [] } = useAdminApprovalGroups()
  const updateDefaultGroupManager = useUpdateDefaultGroupManager()

  // Workday worker IDs that are already the default group manager for another
  // group. The API is the authoritative guard (see the uniqueness validation on
  // ApprovalGroup), but disabling them here saves admins a round trip.
  const groupNameByClaimedWorkdayWorkerId = useMemo(
    () =>
      new Map(
        approvalGroups
          .filter((group) => group.id !== approvalGroup.id)
          .flatMap((group) =>
            group.defaultForDirectReportsOfEmployee
              ? [
                  [
                    group.defaultForDirectReportsOfEmployee.workdayWorkerId,
                    group.name,
                  ] as [number, string],
                ]
              : [],
          ),
      ),
    [approvalGroups, approvalGroup.id],
  )

  const trimmedWorkdayWorkerId = workdayWorkerId.trim()
  const isWellFormed =
    trimmedWorkdayWorkerId === '' ||
    WORKDAY_WORKER_ID_PATTERN.test(trimmedWorkdayWorkerId)

  const selectedEmployee = employees.find(
    (employee) => String(employee.workdayWorkerId) === trimmedWorkdayWorkerId,
  )

  // We can't always verify a hand-typed ID, since the manager may not be a Time
  // Logger employee, so this warns rather than blocks. It stays quiet for the
  // already-saved value, which may well be a manager we've never seen before.
  const showUnrecognizedWarning =
    isWellFormed &&
    trimmedWorkdayWorkerId !== '' &&
    !selectedEmployee &&
    trimmedWorkdayWorkerId !== String(currentWorkdayWorkerId)

  const selectEmployee = (employee: TWorker) => {
    setWorkdayWorkerId(String(employee.workdayWorkerId))
    setErrors([])
  }

  const save = async () => {
    if (!isWellFormed) {
      setErrors([
        t(
          'features.approvalGroupsAdmin.approvalGroupDetails.workdayWorkerIdMustBeANumber',
        ),
      ])
      return
    }

    try {
      await updateDefaultGroupManager.mutateAsync({
        id: approvalGroup.id,
        directReportsOfWid:
          trimmedWorkdayWorkerId === '' ? null : Number(trimmedWorkdayWorkerId),
      })

      onClose()
      onSuccess()
    } catch (error) {
      const serverErrors = serverErrorsFrom(error)

      if (serverErrors) {
        setErrors(serverErrors)
        return
      }

      onError()
    }
  }

  return (
    <Modal
      closeOnBackgroundClick={false}
      hidden={false}
      onClose={onClose}
      title={t(
        'features.approvalGroupsAdmin.approvalGroupDetails.editDefaultGroupManager',
      )}
      variant="custom"
      content={
        <ModalForm
          className="w-[520px]"
          isSaving={updateDefaultGroupManager.isLoading}
          onCancel={onClose}
          onSave={() => void save()}
          fields={
            <>
              <div className="flex flex-col">
                <span className="mb-2 text-sm font-semibold">
                  {t(
                    'features.approvalGroupsAdmin.approvalGroupDetails.selectAnEmployee',
                  )}
                </span>
                <EntityPicker
                  filter={(employee, search) => {
                    const query = search.toLowerCase()
                    return (
                      employee.fullName.toLowerCase().includes(query) ||
                      String(employee.workdayWorkerId).includes(query)
                    )
                  }}
                  isSelected={(employee, selectedIds) =>
                    selectedIds.has(employee.workdayWorkerId)
                  }
                  onEnter={selectEmployee}
                  placeholder={t('common.search')}
                  renderNoItemSelected={() => (
                    <p className="text-xs text-neutral-500">
                      {t(
                        'features.approvalGroupsAdmin.approvalGroupDetails.noEmployeeSelected',
                      )}
                    </p>
                  )}
                  renderSearchItem={(employee, closeDropdown) => {
                    const claimedByGroupName =
                      groupNameByClaimedWorkdayWorkerId.get(
                        employee.workdayWorkerId,
                      )

                    return (
                      <EntityItem
                        key={employee.workdayWorkerId}
                        disabled={Boolean(claimedByGroupName)}
                        onClick={() => {
                          selectEmployee(employee)
                          // Only one manager can be picked here, so close the
                          // dropdown on selection rather than leaving it open.
                          closeDropdown()
                        }}
                        primaryText={employee.fullName}
                        secondaryText={
                          claimedByGroupName
                            ? t(
                                'features.approvalGroupsAdmin.approvalGroupDetails.alreadyDefaultGroupManagerFor',
                                {
                                  id: employee.workdayWorkerId,
                                  name: claimedByGroupName,
                                },
                              )
                            : employee.workdayWorkerId
                        }
                      />
                    )
                  }}
                  renderSelectedItem={(employee) => (
                    <EntityItem
                      key={employee.workdayWorkerId}
                      className="bg-neutral-100 p-3 rounded-lg hover:border-transparent"
                      onClick={() => setWorkdayWorkerId('')}
                      primaryText={employee.fullName}
                      removable={true}
                      secondaryText={employee.workdayWorkerId}
                    />
                  )}
                  selectedIds={
                    selectedEmployee
                      ? new Set([selectedEmployee.workdayWorkerId])
                      : new Set<number>()
                  }
                  useState={() => [search, setSearch]}
                  useQuery={useEmployees}
                />
              </div>
              <Field
                className="mt-8"
                htmlFor="default-group-manager-workday-worker-id"
                label={t('common.workdayWorkerId')}
                errors={errors}
              >
                {(id) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    onChange={(event) => {
                      setWorkdayWorkerId(event.currentTarget.value)
                      setErrors([])
                    }}
                    value={workdayWorkerId}
                  />
                )}
              </Field>
              <p className="text-xs text-neutral-600">
                {t(
                  'features.approvalGroupsAdmin.approvalGroupDetails.workdayWorkerIdHelp',
                )}
              </p>
              {showUnrecognizedWarning && (
                <p className="text-xs text-warning-700">
                  {t(
                    'features.approvalGroupsAdmin.approvalGroupDetails.unrecognizedWorkdayWorkerIdWarning',
                  )}
                </p>
              )}
            </>
          }
        />
      }
    />
  )
}
